import type { IBookingOnboardingSubmission } from '@/_apis_/booking/type';
import { Box, Collapse, Divider, Grid, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import type { BookingOnboardingTableItem } from './BookingFormsTable';

interface BookingFormTableRowProps {
    data: BookingOnboardingTableItem;
}

const TRUNCATED_CELL_SX = {
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
} as const;

const TableCellText = ({ value }: { value: string }): JSX.Element => (
    <Tooltip title={value === '-' ? '' : value}>
        <Typography variant="body2" sx={TRUNCATED_CELL_SX}>
            {value}
        </Typography>
    </Tooltip>
);

const DetailItem = ({ label, value }: { label: string; value: string }): JSX.Element => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {value}
        </Typography>
    </Box>
);

const getPasswordValue = (submission: IBookingOnboardingSubmission): string => {
    return submission.password?.trim() ? submission.password : '-';
};

export const BookingFormTableRow = ({ data }: BookingFormTableRowProps): JSX.Element => {
    const [isRowOpen, setIsRowOpen] = useState(false);

    return (
        <>
            <TableRow
                hover
                tabIndex={-1}
                onClick={() => setIsRowOpen((currentValue) => !currentValue)}
                sx={{
                    '&:nth-of-type(even)': {
                        backgroundColor: 'background.neutral'
                    },
                    '&:hover': {
                        backgroundColor: 'action.hover'
                    },
                    cursor: 'pointer'
                }}
            >
                <TableCell>
                    <TableCellText value={data.restaurantName} />
                </TableCell>
                <TableCell>
                    <TableCellText value={data.ownerManagerName} />
                </TableCell>
                <TableCell>
                    <TableCellText value={data.contact} />
                </TableCell>
                <TableCell>
                    <TableCellText value={data.email} />
                </TableCell>
                <TableCell>
                    <TableCellText value={data.bookingType} />
                </TableCell>
                <TableCell>
                    <TableCellText value={data.createdAtDisplay} />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={isRowOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Restaurant Details
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        <DetailItem label="Restaurant" value={data.restaurantName} />
                                        <DetailItem label="Owner / Manager" value={data.ownerManagerName} />
                                        <DetailItem label="Email" value={data.email} />
                                        <DetailItem label="Contact" value={data.contact} />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Booking Rules
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        <DetailItem label="Dining Duration" value={data.diningDuration} />
                                        <DetailItem label="Slot Interval" value={data.bookingSlotInterval} />
                                        <DetailItem label="Minimum Advance Booking Time" value={data.minimumAdvanceBookingTime} />
                                        <DetailItem label="Time Zone" value={data.timezone} />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Capacity
                                    </Typography>
                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                        <DetailItem label="Session Max Capacity" value={data.sessionMaxCapacity} />
                                        <DetailItem label="Max Pax Per Booking" value={data.maxPaxPerBooking} />
                                        <DetailItem label="Booking Type" value={data.bookingType} />
                                        <DetailItem label="Password" value={getPasswordValue(data.rawSubmission)} />
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Divider />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <DetailItem label="Created At" value={data.createdAtDisplay} />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <DetailItem label="Updated At" value={data.updatedAtDisplay} />
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};
